import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { assistantService } from '../services/assistantService';
import type { Conversation, UpdateConversationRequest } from '../types/assistant';

const CONVERSATIONS_KEY = ['assistant-conversations'];
const messagesKey = (id: string) => ['assistant-messages', id];

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useConversationList() {
  return useQuery({
    queryKey: CONVERSATIONS_KEY,
    queryFn: () => assistantService.listConversations({ limit: 50 }),
    staleTime: 30_000,
  });
}

export function useConversationMessages(conversationId: string | null) {
  return useQuery({
    queryKey: messagesKey(conversationId ?? ''),
    queryFn: () => assistantService.getMessages(conversationId!),
    enabled: !!conversationId,
    staleTime: 10_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (title?: string) => assistantService.createConversation(title ? { title } : undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    },
  });
}

export function useUpdateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateConversationRequest }) =>
      assistantService.updateConversation(id, data),
    onMutate: async ({ id, data }) => {
      await qc.cancelQueries({ queryKey: CONVERSATIONS_KEY });
      const prev = qc.getQueryData<Conversation[]>(CONVERSATIONS_KEY);
      if (prev) {
        qc.setQueryData<Conversation[]>(
          CONVERSATIONS_KEY,
          prev.map(c =>
            c.id === id
              ? {
                  ...c,
                  ...(data.title !== undefined ? { title: data.title } : {}),
                  ...(data.is_pinned !== undefined ? { is_pinned: data.is_pinned } : {}),
                }
              : c
          )
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(CONVERSATIONS_KEY, ctx.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    },
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => assistantService.deleteConversation(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: CONVERSATIONS_KEY });
      const prev = qc.getQueryData<Conversation[]>(CONVERSATIONS_KEY);
      if (prev) {
        qc.setQueryData<Conversation[]>(
          CONVERSATIONS_KEY,
          prev.filter(c => c.id !== id)
        );
      }
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(CONVERSATIONS_KEY, ctx.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    },
  });
}

/**
 * Returns invalidation helpers for the assistant queries.
 */
export function useAssistantQueryHelpers() {
  const qc = useQueryClient();
  return {
    invalidateConversations: () => qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY }),
    invalidateMessages: (id: string) => qc.invalidateQueries({ queryKey: messagesKey(id) }),
  };
}
